// @flow strict-local
import type {AnswerId, Id} from 'src/types/chatbot';

import * as React from 'react';
import useI18n from 'src/hooks/useI18n';

import classify from 'src/utils/classify';
import makeClassNameComponent, {
  type ClassNameComponent,
} from 'src/utils/makeClassNameComponent';
import {useHoverTip} from 'src/components/lib/mouse-tip/mouse-tip.jsx';

import {
  FormLabelMedium,
  FormLabelSmall,
} from '@spaced-out/ui-design-system/lib/components/Text';
import {Truncate} from '@spaced-out/ui-design-system/lib/components/Truncate';
import {Input as GenesisInput} from '@spaced-out/ui-design-system/lib/components/Input';
import {Chip} from '@spaced-out/ui-design-system/lib/components/Chip';
import {ClickAway} from '@spaced-out/ui-design-system/lib/utils/click-away';
import CaretDownIcon from 'src/images/icons/caret-down.svg?noAttrs';

import WarningIcon from 'src/images/icons/warning-icon.svg';

import css from './common.css';
import nCss from 'src/components/conversation-builder/node-editor-v2/node-editor-v2.css';


export const TitleText: ClassNameComponent<> = makeClassNameComponent(
  css.titleText,
);
export const DescriptorText: ClassNameComponent<'span'> =
  makeClassNameComponent(css.descriptorText, 'span');

export function HoverError({
  className,
  error,
  errors,
}: {
  className?: ?string,
  error?: string,
  errors?: Array<string>,
}): React.Node {
  const hoverProps = useHoverTip({
    content: errors != null ? errors.join(',') : error ?? ' ',
    fixedTo: 'top',
  });

  return (
    <div {...hoverProps} className={className}>
      <WarningIcon />
    </div>
  );
}

export const getOptOutMessage = (t: (string, string) => string): string =>
  t(
    'Reply_STOP_to_quit_HELP_for_info_MsgDataRatesMayApply',
    'Reply STOP to quit, HELP for info. Msg&DataRatesMayApply',
  );

export function OptOutMessage({
  className,
  ...props
}: React.ElementConfig<'div'>): React.Node {
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;
  const optOutMessage = getOptOutMessage(t);
  const hoverProps = useHoverTip({
    content:
      'This sentence cannot be deleted and will always show up at the end of the message.',
    fixedTo: 'right',
  });
  return (
    <div
      {...props}
      {...hoverProps}
      className={classify(css.optOutMessage, className)}
    >
      {optOutMessage}
    </div>
  );
}

type MixVariableValue = string | boolean | {name: string, node_id?: Id};
type MixVariableToken = {
  id?: AnswerId,
  value: MixVariableValue,
  label?: string,
  type: string,
  disabled?: boolean,
  writeback_field_value?: string,
};

export const MixVariableInput = ({
  dropdownOptionGroups,
  values,
  handleSelectToken,
  handleRemoveToken,
  allowArbitraryValues,
  disabled = false,
  classNames = {},
}: {
  dropdownOptionGroups: {
    groupTitle: string,
    groupType: string,
    options: {
      value: string,
      label: string,
      entityRelationshipPath?: string[],
    }[],
  }[],
  values: MixVariableToken[],
  handleSelectToken: (string, string, string) => void,
  handleRemoveToken: (MixVariableToken) => void,
  allowArbitraryValues: boolean,
  disabled: boolean,
  classNames?: {dropdown: string},
}): React.Node => {
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;
  const [searchTerm, setSearchTerm] = React.useState('');

  const filteredDropdownOptionGroups = React.useMemo(
    () =>
      dropdownOptionGroups.map((groupData) => ({
        ...groupData,
        // $FlowFixMe[incompatible-type] TokenOption problems
        options: groupData.options.filter(
          (option) =>
            // $FlowFixMe[unnecessary-optional-chain]
            String(option?.label ? option.label : '')
              .toLowerCase()
              .includes(searchTerm.toLowerCase()) ||
            // $FlowFixMe[unnecessary-optional-chain]
            String(option?.value ? option.value : '')
              .toLowerCase()
              .includes(searchTerm.toLowerCase()),
        ),
      })),
    [searchTerm],
  );

  return (
    <ClickAway>
      {({isOpen, onOpen, triggerRef, boundaryRef}) => (
        <div className={nCss.jobMatchCriteriaContainer} ref={triggerRef}>
          <div
            className={classify(nCss.jobMatchCriteriaInputContainer, {
              [nCss.jobMatchCriteriaInputContainerWithDropdown]: isOpen,
            })}
            onClick={disabled ? undefined : onOpen}
          >
            <div className={nCss.jobMatchCriteriaSelected}>
              <Truncate line={1} showFullTextOnHover>
                {values.map(({label}) => label).join(', ')}
              </Truncate>
            </div>
            {!disabled && (
              <div className={nCss.chevron}>
                <CaretDownIcon className={classify(isOpen && nCss.chevronUp)} />
              </div>
            )}
          </div>
          {isOpen && (
            <div
              className={classify(
                nCss.jobMatchCriteriaDropdown,
                classNames.dropdown,
              )}
              ref={boundaryRef}
            >
              <GenesisInput
                classNames={{box: nCss.jobMatchCriteriaInput}}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                iconLeftName="search"
                autoFocus
              />

              <div className={nCss.mixedVariableDropdownChip}>
                {values.map((val) => (
                  <Chip
                    onDismiss={() => handleRemoveToken(val)}
                    semantic="primary"
                    dismissable
                    key={val.toString()}
                  >
                    {/* $FlowFixMe[unnecessary-optional-chain] */}
                    {val?.label ? String(val.label) : ''}
                  </Chip>
                ))}
              </div>
              {filteredDropdownOptionGroups.map(
                ({groupTitle, groupType, options}) => {
                  if (!options.length) {
                    return null;
                  }
                  return (
                    <>
                      <FormLabelSmall color="tertiary">
                        {groupTitle ?? ''}
                      </FormLabelSmall>
                      {options.map((option) => {
                        if (!option.label || !option.value) {
                          return null; //to appease flow
                        }
                        const {label, value} = option;
                        const alreadySelected = values.find(
                          (val) => val.label === label,
                        );

                        return (
                          <div
                            className={classify(
                              nCss.mixedVariableDropdownOption,
                              {
                                [nCss.mixedVariableDropdownOptionSelected]:
                                  alreadySelected,
                              },
                            )}
                            onClick={(e) => {
                              e.preventDefault();
                              if (!alreadySelected) {
                                setSearchTerm('');
                                handleSelectToken(label, value, groupType);
                              }
                            }}
                          >
                            <FormLabelMedium>
                              <Truncate line={1} showFullTextOnHover>
                                {label ? String(label) : ''}
                              </Truncate>
                            </FormLabelMedium>
                            {groupType === 'ats_variables' && (
                              <FormLabelSmall color="tertiary">
                                {/* $FlowFixMe */}
                                {option.entityRelationshipPath
                                  ?.slice(1)
                                  .reverse()
                                  .join('/')}
                              </FormLabelSmall>
                            )}
                          </div>
                        );
                      })}
                    </>
                  );
                },
              )}
              {allowArbitraryValues && searchTerm && (
                <div
                  className={nCss.mixedVariableDropdownOption}
                  onClick={(e) => {
                    e.preventDefault();
                    setSearchTerm('');
                    handleSelectToken(searchTerm, searchTerm, 'default_values');
                  }}
                >
                  <FormLabelMedium>{`${t(
                    'Create',
                    'Create',
                  )}: ${searchTerm}`}</FormLabelMedium>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </ClickAway>
  );
};
